import type {
  AgentHarnessManifest,
  AgentProviderProfile,
  ClassificationResult,
  ContextSlice,
  DaxPreviewRequest,
  DaxPreviewResponse,
  GraphViewModel,
  KPIConcept,
  KPIContractVersion,
  KPIProjection,
  KPIVariant,
  ImplementationBinding,
  ObservedMeasure,
  SandboxMetric,
  ScanJob,
  ScopePreflight,
  WorkspaceScope,
  ResolvedWorkspace,
} from '@metricgraph/contracts';
import { staticDemoApi } from './static-demo-api.js';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  if (import.meta.env.VITE_METRICGRAPH_STATIC_DEMO === 'true') {
    return staticDemoApi<T>(path, init);
  }
  const contentHeaders = init?.body ? { 'content-type': 'application/json' } : {};
  const response = await fetch(path, {
    ...init,
    headers: { ...contentHeaders, ...init?.headers },
  });
  const payload = (await response.json()) as T & { message?: string };
  if (!response.ok)
    throw new ApiError(payload.message ?? `Request failed (${response.status}).`, response.status);
  return payload;
}

export interface OverviewData {
  estate: {
    workspaces: number;
    semanticModels: number;
    reports: number;
    measures: number;
    kpis: number;
    dimensions?: number;
    orphanedReports?: number;
  };
  attention: {
    pendingReviews: number;
    conflicts: number;
    placeholders?: number;
    expiringSandbox: number;
    coverageGaps: number;
  };
  coverage: {
    inventory: number;
    semanticMetadata: number;
    visualUsage: number;
    dependency: number;
    limitations: string[];
    limitationSummary?: Array<{
      reason: string;
      count: number;
      affectedSubjects: string[];
      severity: 'info' | 'warning' | 'critical';
      recommendedAction: string;
    }>;
  };
  recentActivity: Array<{
    id: string;
    action: string;
    subjectType: string;
    occurredAt: string;
    actorId: string;
  }>;
}

export interface KPIRecordData {
  concept: KPIConcept;
  versions: KPIContractVersion[];
  variants: KPIVariant[];
  projections: KPIProjection[];
  contextSlices: ContextSlice[];
  implementationBindings: ImplementationBinding[];
  relatedMeasures: ObservedMeasure[];
  activeResolution: unknown;
}

export interface ReviewCaseView {
  id: string;
  type: string;
  triageType: 'GOVERNED' | 'CANDIDATE' | 'CONFLICTING' | 'DUPLICATE' | 'PLACEHOLDER';
  leftMeasureId: string;
  rightMeasureId: string | null;
  score: number;
  reasons: string[];
  decision: string;
  decidedBy: string | null;
  decidedAt: string | null;
  createdAt: string;
  updatedAt: string;
  left: ObservedMeasure | null;
  right: ObservedMeasure | null;
  leftUsage?: {
    reportCount: number;
    visualCount: number;
    viewCount?: number;
    viewerCount?: number;
    reports: string[];
    workspaces: string[];
  } | null;
  rightUsage?: {
    reportCount: number;
    visualCount: number;
    viewCount?: number;
    viewerCount?: number;
    reports: string[];
    workspaces: string[];
  } | null;
  governanceContext?: {
    source: 'BINDING' | 'NAME_MATCH' | 'UNMAPPED';
    kpiId: string | null;
    kpiName: string | null;
    domain: string | null;
    lifecycle: string | null;
    criticality: string | null;
    contractVersionId: string | null;
    contractDefinition: string | null;
    conformance: string | null;
    validationState: string | null;
    semanticModelEndorsement: string | null;
    reportEndorsements: string[];
    variants: string[];
    projections: string[];
  } | null;
  reviewPacket?: {
    proposedBusinessDescription: string;
    proposedFormula: string;
    formulaRationale: string;
    approvalSteps: string[];
    endorsementSteps: string[];
    impactSimulation: {
      baselineValue: number;
      proposedValue: number;
      deltaPercent: number;
      viewerCount: number;
      viewCount: number;
      reportCount: number;
      visualCount: number;
      confidence: string;
    };
    impactWorkspaces: Array<{
      workspaceName: string;
      reportCount: number;
      visualCount: number;
      viewerCount: number;
      viewCount: number;
      reports: string[];
    }>;
    notificationDraft: string;
  } | null;
}

export interface MeasureView extends ObservedMeasure {
  classification: ClassificationResult | null;
  modelName?: string;
  workspaceName?: string;
  usageCount?: number;
  reportCount?: number;
  inboundDependencyCount?: number;
  outboundDependencyCount?: number;
}

export interface Paginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface MatrixCell {
  measureId: string;
  measureName: string;
  dimensionId: string;
  dimensionName: string;
  reportCount: number;
  visualCount: number;
}

export interface SearchResult {
  id: string;
  label: string;
  kind: 'KPI' | 'MEASURE' | 'REPORT';
  href: string;
  context: string;
}

export interface SandboxView extends SandboxMetric {
  expiryState: string;
}

export interface GovernanceScore {
  key: string;
  label: string;
  numerator: number;
  denominator: number;
  score: number;
}

export interface GovernanceMaturity {
  level: 100 | 200 | 300 | 400 | 500;
  label: string;
  narrative: string;
}

export interface GovernancePriorityAction {
  key: string;
  title: string;
  score: number;
  gap: number;
  impact: string;
  nextStep: string;
  targetMaturityLevel: 200 | 300 | 400 | 500;
  route: string;
}

export interface CapabilityView {
  capability: string;
  state: string;
  provider: string;
  limitation: string | null;
  trustPlane: string;
  identityClass: string;
  fallbackChain: string[];
}

export interface IdentityPolicyView {
  trustPlane: string;
  allowedIdentityClasses: string[];
  interactiveUserRequired: boolean;
  rlsPolicy: string;
  enabled: boolean;
}

export interface AgentConfigurationView {
  provider: AgentProviderProfile;
  harness: AgentHarnessManifest;
  setup: {
    foundry: {
      recommended: boolean;
      authMode: string;
      requiredServerSettings: string[];
      credentialSource: string;
      tokenScope: string;
    };
    custom: {
      recommended: boolean;
      authMode: string;
      requiredServerSettings: string[];
      credentialSource: string;
      tokenScope: null;
    };
  };
  manualDaxAvailable: boolean;
  naturalLanguageAvailable: boolean;
  evidenceBoundary: string;
}

export type ResolutionView = ResolvedWorkspace;

export const queries = {
  session: () =>
    api<{ actor: { id: string; roles: string[]; authenticationMode: string } }>('/api/v1/session'),
  overview: () => api<OverviewData>('/api/v1/overview'),
  kpis: () => api<{ items: KPIConcept[] }>('/api/v1/kpis'),
  kpi: (id: string) => api<KPIRecordData>(`/api/v1/kpis/${id}`),
  measures: (
    input: { page?: number; pageSize?: number; q?: string; classification?: string } = {},
  ) =>
    api<Paginated<MeasureView>>(
      `/api/v1/measures?${new URLSearchParams(
        Object.entries(input).flatMap(([key, value]) =>
          value === undefined || value === '' ? [] : [[key, String(value)]],
        ),
      ).toString()}`,
    ),
  reviews: (
    input: {
      page?: number;
      pageSize?: number;
      triage?: ReviewCaseView['triageType'];
    } = {},
  ) =>
    api<Paginated<ReviewCaseView>>(
      `/api/v1/review-cases?${new URLSearchParams(
        Object.entries(input).flatMap(([key, value]) =>
          value === undefined ? [] : [[key, String(value)]],
        ),
      ).toString()}`,
    ),
  matrix: () => api<{ cells: MatrixCell[]; coverageState: string }>('/api/v1/matrix'),
  search: (q: string) =>
    api<{ items: SearchResult[] }>(`/api/v1/search?q=${encodeURIComponent(q)}`),
  sandbox: () => api<{ items: SandboxView[] }>('/api/v1/sandbox'),
  governance: () =>
    api<{
      scores: GovernanceScore[];
      overallScore: number;
      maturity: GovernanceMaturity;
      priorityActions: GovernancePriorityAction[];
    }>('/api/v1/governance-health'),
  capabilities: () =>
    api<{
      provider: string;
      capabilities: CapabilityView[];
      identityPolicies: IdentityPolicyView[];
      liveTenantValidated: boolean;
    }>('/api/v1/connections/capabilities'),
  agentConfiguration: () => api<AgentConfigurationView>('/api/v1/agent/configuration'),
  previewDax: (input: DaxPreviewRequest) =>
    api<DaxPreviewResponse>('/api/v1/agent/dax/preview', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  scopes: () => api<{ items: WorkspaceScope[] }>('/api/v1/scopes'),
  graph: (
    focus: string,
    lens: string,
    depth: number,
    direction: 'UPSTREAM' | 'DOWNSTREAM' | 'BOTH' = 'BOTH',
  ) =>
    api<GraphViewModel>(
      `/api/v1/graph/${encodeURIComponent(focus)}?lens=${lens}&depth=${depth}&direction=${direction}&limit=120`,
    ),
  resolve: (names: string[]) =>
    api<{ items: ResolutionView[] }>('/api/v1/connections/fabric/resolve', {
      method: 'POST',
      body: JSON.stringify({ names }),
    }),
  preflight: (workspaceIds: string[]) =>
    api<ScopePreflight>('/api/v1/connections/fabric/preflight', {
      method: 'POST',
      body: JSON.stringify({ workspaceIds }),
    }),
  createScope: (input: {
    name: string;
    tenantId: string;
    workspaceIds: string[];
    inputNames: string[];
    externalDependencyPolicy: string;
  }) =>
    api<{ item: WorkspaceScope }>('/api/v1/scopes', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  startScan: (scopeId: string) =>
    api<{ item: ScanJob }>(`/api/v1/scopes/${scopeId}/scans`, { method: 'POST' }),
  scan: (jobId: string) => api<{ item: ScanJob }>(`/api/v1/scans/${jobId}`),
  decideReview: (id: string, reason: string, decision: 'APPROVED' | 'REJECTED' = 'APPROVED') =>
    api<{ item: { id: string; decision: string } }>(`/api/v1/review-cases/${id}/decision`, {
      method: 'POST',
      body: JSON.stringify({
        subjectType: 'RECONCILIATION_CASE',
        subjectId: id,
        decision,
        reason,
        evidenceLinks: [],
      }),
    }),
  promoteCandidateDraft: (reviewCaseId: string, rationale: string) =>
    api<{
      draft: {
        concept: KPIConcept;
        contractVersion: KPIContractVersion;
        inheritedEndorsement: false;
        sourceReviewCaseId: string;
      };
      rationale: string;
    }>(`/api/v1/review-cases/${encodeURIComponent(reviewCaseId)}/promote-draft`, {
      method: 'POST',
      body: JSON.stringify({ rationale }),
    }),
  promoteSandbox: (sandboxMetricId: string, proposedName: string, rationale: string) =>
    api<{ draft: SandboxMetric; rationale: string }>('/api/v1/sandbox/promote', {
      method: 'POST',
      body: JSON.stringify({ sandboxMetricId, proposedName, rationale }),
    }),
  closeSandbox: (id: string, action: 'archive' | 'dispose', reason: string) =>
    api<{ item: SandboxMetric }>(`/api/v1/sandbox/${id}/${action}`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
};
